<template>
  <component
    :is="tag"
    :type="tag === 'button' ? 'button' : undefined"
    :href="href"
    class="btn"
    :class="{
      active: active,
      'btn-primary': primary,
      'btn-secondary': secondary,
      'btn-danger': danger,
      'btn-danger-text': dangerText,
      'btn-cta': cta,
      'btn-block': block,
      'btn-sm': sm,
      'btn-xs': xs,
      'btn-form': form,
      'btn-nav': nav,
      'btn-pill': pill,
      'btn-dropdown': dropdown,
      'btn-max-width': maxWidth,
    }"
    @click="onClick"
    @touchstart="onTouchStart"
    @mousedown="onTouchStart"
  >
    <slot></slot>
    <template v-if="dropdown">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="9"
        height="9"
        fill="currentColor"
        class="bi bi-caret-down-fill"
        viewBox="0 0 16 16"
      >
        <path
          d="M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"
        />
      </svg>
    </template>
  </component>
</template>

<script>
export default {
  name: "Button",
  props: {
    primary: Boolean,
    secondary: Boolean,
    danger: Boolean,
    dangerText: Boolean,
    cta: Boolean,
    block: Boolean,
    active: Boolean,
    sm: Boolean,
    xs: Boolean,
    form: Boolean,
    nav: Boolean,
    pill: Boolean,
    dropdown: Boolean,
    href: String,
    maxWidth: Boolean,
  },
  computed: {
    tag() {
      return this.href ? "a" : "button";
    },
  },
  methods: {
    onClick: function () {
      this.$emit("click");
    },
    onTouchStart: function (event) {
      const button = event.currentTarget;
      button.classList.add("pressed");
      setTimeout(function () {
        button.classList.remove("pressed");
      }, 300);
    },
  },
};
</script>

<style lang="scss" scoped>
.btn {
  --color: var(--app-btn-color);
  --bg-color: transparent;
  --hover-bg: var(--app-btn-hover-bg);
  --height: 44px;
  display: inline-block;
  flex-shrink: 0;
  padding-right: 0.5rem;
  padding-left: 0.5rem;
  height: var(--height);
  min-width: var(--height);
  text-align: center;
  text-decoration: none;
  line-height: var(--height);
  background-color: var(--bg-color);
  border: 0;
  border-radius: 8px;
  color: var(--color);
  font-weight: 700;
  transition: background-color 0.3s;
  user-select: none;
  white-space: nowrap;

  &.pressed,
  &.active {
    background-color: var(--hover-bg);
  }

  &:hover,
  &:active {
    color: var(--color);
    text-decoration: none;
  }

  &:focus {
    outline: 0;
  }

  &:focus-visible {
    background-color: var(--hover-bg);
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.4;
  }

  > svg {
    margin-top: -4px;
    pointer-events: none;
  }
}

.btn-primary {
  --color: var(--app-active);
  --hover-bg: var(--app-btn-hover-bg-light);
  font-weight: 700;
}

.btn-secondary {
  --color: var(--app-active);
  --hover-bg: var(--app-btn-hover-bg-light);
  font-weight: 400;
}

.btn-danger {
  --color: #fff;
  --bg-color: #eb486b !important;
  --hover-bg: #020202;
  font-weight: 700;
}

.btn-danger-text {
  --color: #eb486b;
  --bg-color: transparent;
  font-weight: 700;
}

.btn-cta {
  --color: var(--app-active);
  --bg-color: var(--app-btn-cta);
  --hover-bg: var(--app-btn-hover-bg-light);
  --height: 50px;
  width: 100%;
  max-width: 400px;
  margin-top: 1rem;
  padding: 0 1rem;
  border-radius: 1rem;
}

.btn-block {
  --bg-color: var(--app-btn-gray);
  --height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 400px;
  margin-top: 1rem;
  padding: 0 1rem;
  border-radius: 1rem;
}

.btn-sm {
  --height: 34px;
  --bg-color: var(--app-btn-gray);
  font-size: 13px;
  margin-right: 2px;
  margin-left: 2px;
}

.btn-xs {
  --height: 26px;
  --bg-color: var(--app-btn-gray);
  font-size: 13px;
  margin-right: 2px;
  margin-left: 2px;
  padding-right: 0.4rem;
  padding-left: 0.4rem;
}

.btn-form {
  --color: var(--app-active);
  --height: 38px;
  font-size: 14px;
  --bg-color: var(--app-btn-gray);
  min-width: 90px;
}

.btn-nav {
  padding-right: 0;
  padding-left: 0;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
  font-size: 13px;
  font-weight: 400;

  &.pressed {
    background-color: transparent;
  }

  &.active {
    font-weight: 700;
    border-bottom: 4px solid #42b983;
    background-color: transparent;
  }
}

.btn-pill {
  --height: 36px;
  border: 1px solid #ccc;
  border-radius: 999px;
  font-size: 13px;
  width: 90px;

  &.active {
    font-weight: 700;
    background-color: #42b983;
    border-color: transparent;
    color: #fff;
  }
}

.bi-chevron-down {
  margin-left: -1px;
}

.btn-max-width {
  max-width: 200px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
